/**
 * THE DECK-BUILDING RULES, MIRRORED FROM THE ENGINE.
 *
 * ⭐ Alex, 2026-08-14: *"there is also a build a deck feature that is extremely important. We must
 * ensure that it is working and following the build a deck rules."*
 *
 * ⭐ **THE RULES ARE NOT OURS TO INVENT - THEY ARE ALREADY IN `Revival.Core`**, and they are better
 * than they look from outside:
 *
 * | Rule | Where it comes from |
 * |---|---|
 * | 30 to 35 cards | `GameConfig.minCardsInDeck` / `maxCardsInDeck` |
 * | at most `limitInDeck` copies of a card | `CardData.LimitInDeck` |
 * | you must OWN the card | `GameConfig.bypassCardOwnership` is false |
 * | a card's realm is scored against your HERO's realm | `HeroDeckbuildingRules.GetLoyalty` |
 * | that score CHANGES THE CARD'S COST | `GetFoxfirePenalty`: +0 / +1 / +2 |
 * | a NEMESIS-realm card cannot be included at all | `CanIncludeInDeck => Loyalty != Nemesis` |
 * | at most 6 decks | `GameConfig.maxDeckCount` |
 *
 * 🚨 **THE LOYALTY RULE IS THE WHOLE FORMAT AND IT WAS NOT BEING SHOWN ANYWHERE.** The same card
 * costs a different amount depending on the hero you built around, and a quarter of the collection
 * is closed to you outright. That is what stops a deck from being "the 30 strongest cards", and it
 * is the honest answer to Alex's earlier question about whether the realm mechanic adds strategy:
 * it is the strategy. A builder that does not show it is just a list with a counter.
 *
 * ⛔ **THIS IS A MIRROR, NOT A SECOND RULES ENGINE.** It decides what to OFFER and what to explain.
 * The server validates on save, and where they disagree the SERVER IS RIGHT.
 */
import { LOYALTY, LOYALTY_PENALTY, type Loyalty } from "../loyalty.generated";

export interface DeckLimits {
  minCardsInDeck: number;
  maxCardsInDeck: number;
  maxDeckCount: number;
}

/**
 * ⚠️ **DEFAULTS EXIST ONLY SO A CONFIG FETCH FAILURE CANNOT SILENTLY ALLOW AN ILLEGAL DECK.** They
 * are the values the live config actually serves; `loadLimits` replaces them. They are deliberately
 * NOT more permissive than the real ones - a fallback that permits more than the server accepts
 * produces a deck the player cannot save and cannot understand why.
 */
export const DEFAULT_LIMITS: DeckLimits = {
  minCardsInDeck: 30,
  maxCardsInDeck: 35,
  maxDeckCount: 6,
};

/** How a card stands with a hero. `Nemesis` means it cannot go in the deck at all. */
export function loyaltyOf(heroRealm: string | undefined, cardRealm: string | undefined): Loyalty {
  if (!heroRealm || !cardRealm) return "Alliance";
  // Same realm, and the always-allowed realms (Neutral, The Ironworks), are Alliance - the engine
  // checks this BEFORE the wheel, which is why they never appear in the generated rows.
  if (heroRealm === cardRealm) return "Alliance";
  return LOYALTY[heroRealm]?.[cardRealm] ?? "Alliance";
}

/** What the card actually costs in this deck. The printed cost is only the starting point. */
export function effectiveCost(
  baseCost: number,
  heroRealm: string | undefined,
  cardRealm: string | undefined,
): number {
  const l = loyaltyOf(heroRealm, cardRealm);
  // ⚠️ The engine does NOT add a penalty for Nemesis - because a Nemesis card is never in a deck to
  // be paid for. Showing base cost for one would imply it is playable.
  if (l === "Nemesis") return baseCost;
  return baseCost + (LOYALTY_PENALTY[l] ?? 0);
}

export function canInclude(heroRealm: string | undefined, cardRealm: string | undefined): boolean {
  return loyaltyOf(heroRealm, cardRealm) !== "Nemesis";
}

export interface DeckEntry {
  cardId: string;
  copies: number;
  /** From the card database. */
  limitInDeck: number;
  realm?: string | undefined;
}

export interface DeckProblem {
  /** A sentence a player can act on, never a code. */
  message: string;
  /** True when it alone stops the deck being saved. */
  blocking: boolean;
}

/**
 * Everything wrong with the deck as it stands, in the order a player should fix it.
 *
 * ⭐ **IT RETURNS EVERY PROBLEM, NOT THE FIRST.** A builder that reveals one error at a time turns
 * finishing a deck into a guessing game - the player fixes the count, then learns about the copies,
 * then learns about a nemesis card. Say all of it at once.
 */
export function deckProblems(
  entries: DeckEntry[],
  heroRealm: string | undefined,
  limits: DeckLimits,
): DeckProblem[] {
  const problems: DeckProblem[] = [];
  const total = entries.reduce((n, e) => n + e.copies, 0);

  if (total < limits.minCardsInDeck) {
    problems.push({
      message: `${limits.minCardsInDeck - total} more card${
        limits.minCardsInDeck - total === 1 ? "" : "s"} needed (minimum ${limits.minCardsInDeck}).`,
      blocking: true,
    });
  }
  if (total > limits.maxCardsInDeck) {
    problems.push({
      message: `${total - limits.maxCardsInDeck} card${
        total - limits.maxCardsInDeck === 1 ? "" : "s"} over the maximum of ${limits.maxCardsInDeck}.`,
      blocking: true,
    });
  }

  for (const e of entries) {
    const limit = e.limitInDeck > 0 ? e.limitInDeck : 1;
    if (e.copies > limit) {
      problems.push({
        message: `Too many copies of one card: ${e.copies} where ${limit} ${
          limit === 1 ? "is" : "are"} allowed.`,
        blocking: true,
      });
    }
    if (!canInclude(heroRealm, e.realm)) {
      problems.push({
        message: "A card from your hero's nemesis realm cannot be in this deck.",
        blocking: true,
      });
    }
  }

  return problems;
}
